import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import moment from "moment";

import { putBilling, postBilling, getBilling, deleteBilling } from "../../../api/billings";

import BillingInfos from "./BillingInfos";

import PropTypes from "prop-types";

export default function CreateEditBillings({ edit = false, billings, setBillings, device, user }) {
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState(0);
  const steps = edit ? billings.map((_, i) => `situation ${i + 1}`) : ["situation 1"];
  const isLastStep = step === steps.length - 1;
  const isFirstStep = step === 0;

  const [financeId, setFinanceId] = useState("");
  const [situationDescription, setSituationDescription] = useState("");
  const [situationDate, setSituationDate] = useState(null);
  const [waitingDate, setWaitingDate] = useState(null);
  const [startWaitingDate, setStartWaitingDate] = useState(null);
  const [billNumber, setBillNumber] = useState("");
  const [requiredPercentage, setRequiredPercentage] = useState("");
  const [status, setStatus] = useState("");

  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  useEffect(() => {
    if (edit && billings && billings[step] !== undefined) {
      setFinanceId(billings[step].finance_id);
      setSituationDescription(billings[step].situation_description);
      setSituationDate(billings[step].situation_date);
      setWaitingDate(billings[step].waiting_date);
      setStartWaitingDate(billings[step].waiting_date);
      setBillNumber(billings[step].bill_number);
      setRequiredPercentage(billings[step].required_percentage);
      setStatus(billings[step].status);
      setDeleteConfirmation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, billings]);

  async function createBillingAPI() {
    const billing = {
      device_id: device.device_id,
      situation_description: situationDescription,
      situation_date: situationDate ? moment(situationDate).format("YYYY-MM-DD") : null,
      waiting_date: waitingDate ? moment(waitingDate).format("YYYY-MM-DD") : null,
      bill_number: billNumber,
      required_percentage: requiredPercentage,
      status: status,
    };
    await postBilling(billing);
  }

  async function deleteBillingAPI() {
    await deleteBilling(financeId);
  }

  async function updateBillingAPI() {
    const billing = {
      device_id: device.device_id,
      situation_description: situationDescription,
      situation_date: situationDate ? moment(situationDate).format("YYYY-MM-DD") : null,
      waiting_date: waitingDate ? moment(waitingDate).format("YYYY-MM-DD") : null,
      bill_number: billNumber,
      required_percentage: requiredPercentage,
      status: status,
    };
    await putBilling(billing, financeId);
  }

  async function fetchBillings() {
    const response = await getBilling(device.device_id);
    setBillings(response.data);
  }

  useEffect(() => {
    fetchBillings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetData() {
    setSituationDescription("");
    setSituationDate(null);
    setWaitingDate(null);
    setBillNumber("");
    setRequiredPercentage("");
    setStatus("");
  }

  async function deleteBill() {
    await deleteBillingAPI();
    await fetchBillings();
    exit();
  }

  async function cancel() {
    if (edit) {
      await fetchBillings();
    } else {
      resetData();
    }
    exit();
  }

  async function submit() {
    if (edit) {
      await updateBillingAPI();
      await fetchBillings();
    } else {
      await createBillingAPI();
      await fetchBillings();
      resetData();
      exit();
    }
  }

  function exit() {
    setStep(0);
    setOpen(false);
  }

  const next = () => {
    setStep(step + 1);
  };

  const previous = () => setStep(step - 1);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>add</Icon>
        </IconButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={edit ? -12 : -5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={edit ? 1 : 2}>
                    <MDBox>
                      {edit ? (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode ? "inherit" : "white"}
                        >
                          Modification des situations de facturation
                        </MDTypography>
                      ) : (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode ? "inherit" : "white"}
                        >
                          Ajouter une nouvelle situation de facturation
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Veuillez rentrer les informations relatives à la situation de facturation
                    </MDTypography>
                  </MDBox>
                  {edit && (
                    <Stepper
                      sx={{ boxShadow: "none", backgroundImage: "none" }}
                      activeStep={step}
                      alternativeLabel
                    >
                      {steps.map((label) => (
                        <Step
                          sx={{
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                              {
                                color: darkMode && "#344767 !important",
                              },
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                              {
                                color: darkMode && "#344767 !important",
                              },
                          }}
                          key={label}
                        >
                          <StepLabel
                            sx={{
                              "& .MuiStepLabel-label.Mui-active": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepLabel-label.Mui-completed": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepIcon-root.Mui-active": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                              "& .MuiStepIcon-root.Mui-completed": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    <BillingInfos
                      user={user}
                      situationDescription={situationDescription}
                      setSituationDescription={setSituationDescription}
                      situationDate={situationDate}
                      setSituationDate={setSituationDate}
                      waitingDate={waitingDate}
                      setWaitingDate={setWaitingDate}
                      startWaitingDate={startWaitingDate}
                      billNumber={billNumber}
                      setBillNumber={setBillNumber}
                      requirePercentage={requiredPercentage}
                      setRequirePercentage={setRequiredPercentage}
                      status={status}
                      setStatus={setStatus}
                    />
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      <MDBox width="25%">
                        {isFirstStep ? (
                          <MDBox />
                        ) : (
                          !deleteConfirmation && (
                            <MDButton variant="gradient" color="warning" onClick={previous}>
                              Précédent
                            </MDButton>
                          )
                        )}
                      </MDBox>
                      <MDBox textAlign="right" width="75%">
                        <Grid container justifyContent="right" spacing={1.5}>
                          {isLastStep && steps.length > 0 ? (
                            <MDBox />
                          ) : (
                            !deleteConfirmation && (
                              <Grid item>
                                <MDButton variant="gradient" color="info" onClick={next}>
                                  Suivant
                                </MDButton>
                              </Grid>
                            )
                          )}
                          {!deleteConfirmation && (
                            <Grid item>
                              <MDButton variant="gradient" color="warning" onClick={cancel}>
                                Annuler
                              </MDButton>
                            </Grid>
                          )}
                          {!deleteConfirmation && (
                            <Grid item>
                              <MDButton variant="gradient" color="success" onClick={submit}>
                                Envoyer
                              </MDButton>
                            </Grid>
                          )}
                          {edit &&
                            (!deleteConfirmation ? (
                              <Grid item>
                                <MDButton
                                  variant="gradient"
                                  color="error"
                                  onClick={() => setDeleteConfirmation(true)}
                                >
                                  Supprimer la situation
                                </MDButton>
                              </Grid>
                            ) : (
                              <>
                                <Grid item>
                                  <MDButton variant="gradient" color="error" onClick={deleteBill}>
                                    Supprimer
                                  </MDButton>
                                </Grid>
                                <Grid item>
                                  <MDButton
                                    variant="gradient"
                                    color="warning"
                                    onClick={() => setDeleteConfirmation(false)}
                                  >
                                    Annuler
                                  </MDButton>
                                </Grid>
                              </>
                            ))}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

CreateEditBillings.propTypes = {
  edit: PropTypes.bool.isRequired,
};
